/**
 * useInfiniteScroll — asks for the next page while the member is still reading
 * the current one, so a paginated list never makes them wait at the bottom.
 *
 * The sentinel is watched with a `rootMargin` of `leadScreens` viewports, which
 * is what turns this into a prefetch rather than a bottom-of-list reaction: the
 * request starts roughly a screen and a half before the end of the list scrolls
 * into view, and the rows are normally rendered before the member gets there.
 *
 * A fixed distance is not enough on its own. A fling covers thousands of pixels
 * in a few hundred milliseconds, reaches the end of the document while momentum
 * is still running, and the moment the page lands that leftover velocity has
 * somewhere to go and throws the member deep into rows they never chose to skip.
 * So the distance also scales with measured scroll speed: the faster the scroll,
 * the earlier the request, which keeps the end of the list out of reach.
 *
 * Attach `sentinelRef` to an element that sits after the last row, and render a
 * manual control whenever `needsManualTrigger` is true — that covers both a
 * missing IntersectionObserver and a run of pages that answered without adding
 * rows (auto-loading those would repeat the same request forever).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** Viewports of lead time between the request and the end of the list. */
const DEFAULT_LEAD_SCREENS = 1.5;
/** Granularity of the lead margin — see `leadMarginPx`. */
const VIEWPORT_STEP_PX = 200;
/**
 * Scroll speed past which a reader is no longer reading but flinging. Below it
 * the observer alone is enough and no measuring is done per scroll event.
 */
const FAST_SCROLL_PX_PER_MS = 1.2;
/** How far ahead of a fling to fetch, in travel time rather than pixels. */
const VELOCITY_LOOKAHEAD_MS = 700;
/** Ceiling on the speed bonus, so one violent fling cannot fetch the whole feed. */
const MAX_VELOCITY_LEAD_PX = 10000;
/** Weight of the newest sample; smooths jitter without lagging a real fling. */
const VELOCITY_SMOOTHING = 0.4;
/**
 * Consecutive pages that may add nothing before auto-loading gives up. One is
 * not enough: a live feed can legitimately answer with rows already held (the
 * caller de-duplicates), and the cursor still moved, so the next page is worth
 * trying before the member is asked to take over.
 */
const STALL_TOLERANCE = 2;
/**
 * How long to wait for a request to actually start. A surface may ignore the
 * call because it already knows about a request; nothing changes then, so the
 * trigger has to re-arm itself rather than wait for a page that never comes.
 */
const REQUEST_START_TIMEOUT_MS = 1200;

/**
 * How far below the fold the sentinel may sit and still trigger a prefetch.
 *
 * Rounded to a step so the observer is only rebuilt when the viewport changes
 * meaningfully; a mobile address bar sliding away must not churn through
 * observers on every scroll frame.
 */
export const leadMarginPx = (viewportHeight: number, leadScreens: number): number =>
  Math.max(
    VIEWPORT_STEP_PX,
    Math.round((viewportHeight * leadScreens) / VIEWPORT_STEP_PX) * VIEWPORT_STEP_PX,
  );

/**
 * How far ahead of the end of the list to fetch at a given scroll speed.
 *
 * At reading speed this is just the lead margin. Under a fling it grows with
 * velocity, because what matters is not distance but whether the page can land
 * before the member arrives — the end of the document must stay out of reach
 * while momentum is still running.
 */
export const prefetchDistancePx = (
  viewportHeight: number,
  leadScreens: number,
  velocityPxPerMs: number,
): number =>
  leadMarginPx(viewportHeight, leadScreens) +
  Math.min(
    Math.max(velocityPxPerMs, 0) * VELOCITY_LOOKAHEAD_MS,
    MAX_VELOCITY_LEAD_PX,
  );

/** True when the sentinel is within `distance` of the bottom of the viewport. */
export const isWithinDistance = (
  sentinelTop: number,
  viewportHeight: number,
  distance: number,
): boolean => sentinelTop - viewportHeight <= distance;

const supportsObserver = (): boolean =>
  typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function';

export interface UseInfiniteScrollOptions {
  /** False once the server has no further pages. */
  hasNextPage: boolean;
  /** True while a page request is in flight. */
  isFetching: boolean;
  onLoadMore: () => void;
  /** Rows currently rendered — used to notice a page that added nothing. */
  itemCount: number;
  leadScreens?: number;
}

export interface UseInfiniteScrollResult {
  sentinelRef: (node: HTMLElement | null) => void;
  /** True when auto-loading cannot run, so a button has to be offered. */
  needsManualTrigger: boolean;
  loadMore: () => void;
}

export const useInfiniteScroll = ({
  hasNextPage,
  isFetching,
  onLoadMore,
  itemCount,
  leadScreens = DEFAULT_LEAD_SCREENS,
}: UseInfiniteScrollOptions): UseInfiniteScrollResult => {
  // A ref alone would not re-run the observer effect when the sentinel mounts
  // with the first page, so the node is held in state.
  const [sentinel, setSentinel] = useState<HTMLElement | null>(null);
  const [observerUnsupported] = useState(() => !supportsObserver());
  const [stalled, setStalled] = useState(false);

  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  const itemCountRef = useRef(itemCount);
  itemCountRef.current = itemCount;

  const countAtRequestRef = useRef(0);
  /**
   * Asked for a page, but the surface has not reported itself busy yet.
   *
   * This has to stay set until the request is observed starting, not merely
   * until the next frame. Callers decide whether to honour the call by reading
   * their own state, which React has not committed yet, so a second call made a
   * frame later is accepted and fetches the very same page. The duplicate rows
   * are then discarded by the caller's de-duplication, the list does not grow,
   * and that looks exactly like a server with nothing left to give.
   */
  const pendingRef = useRef(false);
  /** A request we asked for is genuinely in flight. */
  const inFlightRef = useRef(false);
  const pendingTimerRef = useRef(0);
  /** Consecutive completed pages that added no rows. */
  const noGrowthRef = useRef(0);
  /** Smoothed scroll speed in px/ms, fed by the listener below. */
  const velocityRef = useRef(0);

  const needsManualTrigger = observerUnsupported || stalled;
  const canAutoLoad = hasNextPage && !isFetching && !needsManualTrigger;
  const canAutoLoadRef = useRef(canAutoLoad);
  canAutoLoadRef.current = canAutoLoad;

  const loadMore = useCallback(() => {
    if (pendingRef.current || inFlightRef.current) return;
    pendingRef.current = true;
    countAtRequestRef.current = itemCountRef.current;
    window.clearTimeout(pendingTimerRef.current);
    pendingTimerRef.current = window.setTimeout(() => {
      pendingRef.current = false;
    }, REQUEST_START_TIMEOUT_MS);
    onLoadMoreRef.current();
  }, []);

  useEffect(() => () => window.clearTimeout(pendingTimerRef.current), []);

  const sentinelRef = useCallback((node: HTMLElement | null) => {
    setSentinel(node);
  }, []);

  useEffect(() => {
    if (!sentinel || observerUnsupported) return;

    let observer: IntersectionObserver | null = null;
    let appliedMargin = -1;

    const connect = () => {
      const margin = leadMarginPx(window.innerHeight, leadScreens);
      if (observer && margin === appliedMargin) return;
      appliedMargin = margin;
      observer?.disconnect();
      observer = new IntersectionObserver(
        entries => {
          const entry = entries[entries.length - 1];
          if (entry?.isIntersecting && canAutoLoadRef.current) loadMore();
        },
        { rootMargin: `0px 0px ${margin}px 0px` },
      );
      observer.observe(sentinel);
    };

    connect();
    window.addEventListener('resize', connect);
    return () => {
      window.removeEventListener('resize', connect);
      observer?.disconnect();
    };
  }, [sentinel, observerUnsupported, leadScreens, loadMore]);

  /**
   * A short page can leave the end of the list still inside the lead margin.
   * The observer stays quiet then, because the intersection never changed, so
   * the follow-up request is issued here instead. The distance is measured a
   * frame later, once the appended rows have been laid out, rather than read
   * back from the observer, which has not reported the new position yet.
   */
  useEffect(() => {
    if (!canAutoLoad) return;
    const frame = window.requestAnimationFrame(() => {
      if (!canAutoLoadRef.current || !sentinel) return;
      const viewportHeight = window.innerHeight;
      const { top } = sentinel.getBoundingClientRect();
      const distance = prefetchDistancePx(viewportHeight, leadScreens, velocityRef.current);
      if (isWithinDistance(top, viewportHeight, distance)) loadMore();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [canAutoLoad, sentinel, leadScreens, loadMore]);

  /**
   * Speed-aware trigger. The observer's margin is a fixed distance, so a fling
   * outruns it, hits the end of the document and then gets thrown forward when
   * the page finally lands. Watching velocity lets the request go out early
   * enough that the member never reaches the end at all.
   *
   * Below fling speed this does nothing but arithmetic — no layout is read —
   * so ordinary scrolling keeps paying the observer's zero main-thread cost.
   */
  useEffect(() => {
    if (!sentinel) return;

    let frame = 0;
    let lastY = window.scrollY;
    let lastTimestamp = performance.now();

    const check = () => {
      frame = 0;
      const velocity = velocityRef.current;
      if (!canAutoLoadRef.current || velocity < FAST_SCROLL_PX_PER_MS) return;
      const viewportHeight = window.innerHeight;
      const { top } = sentinel.getBoundingClientRect();
      const distance = prefetchDistancePx(viewportHeight, leadScreens, velocity);
      if (isWithinDistance(top, viewportHeight, distance)) loadMore();
    };

    const handleScroll = () => {
      const now = performance.now();
      const y = window.scrollY;
      const elapsed = now - lastTimestamp;
      if (elapsed > 0) {
        const instant = Math.abs(y - lastY) / elapsed;
        velocityRef.current =
          velocityRef.current * (1 - VELOCITY_SMOOTHING) + instant * VELOCITY_SMOOTHING;
      }
      lastY = y;
      lastTimestamp = now;
      if (!frame) frame = window.requestAnimationFrame(check);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.cancelAnimationFrame(frame);
    };
  }, [sentinel, leadScreens, loadMore]);

  useEffect(() => {
    if (isFetching) {
      if (pendingRef.current) {
        pendingRef.current = false;
        window.clearTimeout(pendingTimerRef.current);
        inFlightRef.current = true;
      }
      return;
    }
    if (!inFlightRef.current) return;
    inFlightRef.current = false;
    // Only a run of pages that all add nothing means auto-loading is stuck; a
    // single one is normal on a feed that moves under the cursor.
    noGrowthRef.current = itemCount === countAtRequestRef.current ? noGrowthRef.current + 1 : 0;
    setStalled(hasNextPage && noGrowthRef.current >= STALL_TOLERANCE);
  }, [isFetching, hasNextPage, itemCount]);

  /**
   * A shorter list than the one we measured means it was replaced rather than
   * appended to — a tab switch or a refresh. Whatever the previous list did,
   * it says nothing about this one, so auto-loading starts clean.
   */
  const previousCountRef = useRef(itemCount);
  useEffect(() => {
    if (itemCount < previousCountRef.current) {
      noGrowthRef.current = 0;
      countAtRequestRef.current = itemCount;
      setStalled(false);
    }
    previousCountRef.current = itemCount;
  }, [itemCount]);

  return { sentinelRef, needsManualTrigger, loadMore };
};
